import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { RubroService } from '../../../services/rubro/rubro.service';
import { Rubro } from '../../../domain/rubro.domain';

@Component({
  selector: 'app-rubro-edit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    TextareaModule,
    ToggleSwitchModule,
    ButtonModule
  ],
  templateUrl: './rubro-edit.component.html',
  styleUrls: ['./rubro-edit.component.scss']
})
export class RubroEditComponent implements OnInit, OnDestroy {
  rubroForm!: FormGroup;
  loading = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  rubroId: string | null = null;
  rubro = signal<Rubro | null>(null);

  private destroy$ = new Subject<void>();
  private rubroService = inject(RubroService);

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.setupRouteParamListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupRouteParamListener(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const newId = params.get('id');
        if (newId !== this.rubroId) {
          this.loadRubro();
        }
      });
  }

  initForm(): void {
    this.rubroForm = this.fb.group({
      codigo: ['', [Validators.required, Validators.maxLength(255)]],
      nombre: ['', [Validators.required, Validators.maxLength(255)]],
      descripcion: ['', Validators.maxLength(255)],
      active: [true, Validators.required]
    });
  }

  loadRubro(): void {
    const newRubroId = this.route.snapshot.paramMap.get('id');

    if (newRubroId !== this.rubroId) {
      this.rubroForm.reset();
      this.rubro.set(null);
    }

    this.rubroId = newRubroId;

    if (this.rubroId && this.rubroId !== 'nuevo') {
      this.isEditMode.set(true);
      this.loading.set(true);

      this.rubroService.getById(this.rubroId!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (rubro: Rubro) => {
            if (rubro) {
              this.rubro.set(rubro);
              this.rubroForm.patchValue({
                codigo: rubro.codigo || '',
                nombre: rubro.nombre || '',
                descripcion: rubro.descripcion || '',
                active: rubro.active ?? true
              });
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Rubro no encontrado',
                life: 3000
              });
              this.router.navigate(['/rubros']);
            }
            this.loading.set(false);
          },
          error: (error) => {
            this.loading.set(false);
            console.error('Error al cargar rubro:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al cargar los datos del rubro',
              life: 3000
            });
          }
        });
    } else {
      this.isEditMode.set(false);
      // Valores por defecto para nuevo rubro
      this.rubroForm.patchValue({
        active: true
      });
    }
  }

  onSubmit(): void {
    if (this.rubroForm.invalid) {
      this.markFormGroupTouched(this.rubroForm);
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Inválido',
        detail: 'Por favor complete todos los campos requeridos',
        life: 3000
      });
      return;
    }

    const formValue = this.rubroForm.value;

    const rubro: Rubro = {
      codigo: formValue.codigo,
      nombre: formValue.nombre,
      descripcion: formValue.descripcion || null,
      active: formValue.active
    };

    if (this.isEditMode()) {
      rubro.id = parseInt(this.rubroId!, 10);
    }

    this.loading.set(true);

    const operation = this.isEditMode()
      ? this.rubroService.update(this.rubroId!, rubro)
      : this.rubroService.create(rubro);

    operation.subscribe({
      next: (response) => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: this.isEditMode() ? 'Rubro actualizado correctamente' : 'Rubro creado correctamente',
          life: 3000
        });
        setTimeout(() => {
          this.router.navigate(['/rubros']);
        }, 1500);
      },
      error: (error) => {
        this.loading.set(false);
        console.error('Error al guardar rubro:', error);

        let errorMessage = 'Error al guardar el rubro';

        if (error.status === 422 && error.error?.violations) {
          const violations = error.error.violations;
          const fieldErrors: { [key: string]: string } = {};

          violations.forEach((violation: any) => {
            const field = violation.propertyPath;
            const message = violation.message;
            fieldErrors[field] = message;

            if (this.rubroForm.get(field)) {
              this.rubroForm.get(field)?.setErrors({ 'server': message });
              this.rubroForm.get(field)?.markAsTouched();
            }
          });

          if (violations.length === 1) {
            errorMessage = violations[0].message;
          } else {
            errorMessage = `Se encontraron ${violations.length} errores de validación`;
          }
        } else if (error.error?.detail) {
          errorMessage = error.error.detail;
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        this.messageService.add({
          severity: 'error',
          summary: 'Error de Validación',
          detail: errorMessage,
          life: 5000
        });
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/rubros']);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  get pageTitle(): string {
    return this.isEditMode() ? 'Editar Rubro' : 'Nuevo Rubro';
  }
}
